// type = this.call('getType'),
// key = this.state.key

export function removeItemFromSidebar(type, key) {
  type = this.getPlural(type)

  const item = this.call('findFileSidebarItem', type, key)
  if (!item) {
    return
  }
  item.remove()

  if (!this.call('hasItems', type)) {
    this.call('rerenderFileSidebarByType', type)
  }
}

export function removeItemFromCanvas(type, key) {
  if (!this.isCanvas()) {
    this.warn('Removing from canvas requires active canvas page')
  }

  type = this.getPlural(type)

  const Collection = this.getCanvasCollection()
  if (!Collection) {
    return this.error('Canvas Collection was not found')
  }

  delete Collection.context[type][key]
}

export function removeItemFromChosen(type, key) {
  if (!this.isCanvas()) {
    return this.warn('Removing from chosen requires active canvas page')
  }

  type = this.getPlural(type)

  const editor = this.getComponentEditor()
  const functionEditor = editor.parent.FunctionEditor

  if (functionEditor.state.key === key) {
    this.call('activateFunction', null)
  }

  const chosen = this.getChosenKey()
  const chosenContext = this.getChosenElementContext()

  if (chosen) {
    this.getChosenScene().update()
  }

  if (key === this.call('getChosenKey')) {
    this.call('activateChosen', null)
  }

  if (!chosenContext) {
    if (chosen) {
      this.error('Canvas Chosen is set but chosen context was not found')
    }
    return
  }

  if (chosen === key) {
    this.activateChosen(null)
  }

  delete chosenContext[type][key]
}

export function removeItem(type, key, opts = {}) {
  if (!type || !key) {
    return this.error('Type or key is not defined for remove operation')
  }

  type = this.getPlural(type)

  const confirmMsg = `Do you want to remove the "${key}" from ${type}?`
  // TODO(@symbo.ls/windows migration): replace with
  // `this.call('confirmDialog', { onConfirm: () => continueWithRemoval() })`.
  // Same blocker as workspace/packages/shared/methods/component.js's
  // `checkIfReferencedInOtherCanvasItems` — this function returns false
  // synchronously when the user declines, and callers consume that
  // boolean. Migration needs the caller chain made async first.
  if (!opts.skipConfirmation && !window.confirm(confirmMsg)) {
    return false
  }

  const isInDataOrSchema = this.getData(type)[key]
  if (!isInDataOrSchema) {
    this.warn("Trying to remove but can't find in data", type, key)
  }

  this.sdk.deleteItem(type, key, opts)
  this.call('removeItemFromCanvas', type, key)
  this.call('removeItemFromChosen', type, key)

  const testArr = []

  const testFn = () => {
    testArr.push(
      this.testCase('Item exists in root state', 'notExists', this.getItem(key, type)),
      this.testCase(
        'Item exists in canvas context',
        'notExistsIf',
        this.call('isCanvas'),
        this.getCanvasContext()[type][key]
      ),
      this.testCase(
        'Item exists in chosen context',
        'notExistsIf',
        this.call('isCanvas'),
        this.getChosenElementContext()?.[type][key]
      )
    )

    if (!opts.skipSidebar) {
      this.call('removeItemFromSidebar', type, key)
      testArr.push(
        this.testCase(
          'Item exists in sidebar',
          'notExistsIf',
          this.call('isTypeSidebarActive', type),
          this.call('findFileSidebarItem', type, key)
        )
      )
    }
  }

  // run tests if in debugging mode
  this.testAll(`Removing ${key} in ${type}`, testFn)

  return true
}

export function removeCanvasArtboard(type = this.getType(), key, opts = {}) {
  if (!type || !key) {
    return this.error('Type or key is not defined for remove operation')
  }

  type = this.getPlural(type)

  const referencedRemovalPermitted = this.call('checkIfReferencedInOtherCanvasItems', type, key)
  if (!referencedRemovalPermitted) {
    return
  }

  const canvasPageChanges = this.call('returnRemovedCanvasPageChanges', type, key)
  if (canvasPageChanges) {
    opts.append = canvasPageChanges
  }

  const removed = this.call('removeItem', type, key, opts)
  if (!removed) {
    return
  }

  const found = this.findComponentOnCanvas(key)
  if (found?.node) {
    this.getCanvasFrameWindow().gs.removeWidget(found.node)
    found.remove()
  }

  if (referencedRemovalPermitted.length) {
    this.call('refreshComponentsWhereReferencedOnCanvas', key)
  }

  if (this.getSelectedKey() === key) {
    this.activateSelected(null)
  }

  this.testAll(`Removing ${key} in ${type}`, [
    this.testCase('Item exists in root state', 'notExists', this.getSchema(type)[key]),
    this.testCase('Item exists in canvas context', 'notExists', this.getCanvasContext()[type][key]),
    this.testCase('Item exists on canvas', 'notExists', this.findComponentOnCanvas(key)),
    this.testCase(
      'Item was removed from canvas page',
      'notExists',
      this.getActiveCanvasPage() !== 'Default' && this.call('onCurrentCanvasPage', type, key)
    )
    // this.testCase(
    //   'Widget was removed from canvas if found',
    //   'success',
    // )
  ])

  return true
}

export function removeCanvasExecutable(type, key, opts) {
  if (!type || !key) {
    return this.error('Type or key is not defined for remove operation')
  }

  type = this.getPlural(type)

  // TODO: find components that use this function
  // and prompt the confirmation

  const removed = this.call('removeItem', type, key, opts)
  if (!removed) {
    return
  }

  const Canvas = this.getCanvas()

  Canvas.update()
  this.getChosenScene().update()

  return true
}

export function removeAny(type, key, opts) {
  if (!type || !key) {
    return this.error('Type or key is not defined for remove operation')
  }

  type = this.getPlural(type)

  if (this.call('hasArtboard', type)) {
    return this.call('removeCanvasArtboard', type, key, opts)
  } else if (this.call('hasExecutable', type)) {
    return this.call('removeCanvasExecutable', type, key, opts)
  }
  return this.call('removeItem', type, key, opts)
}
